import { useStore } from '@/store';
import { styled } from '@/styles/stitches.config';
import { ComposableStyledComponentProps } from '@/types/composable-styled-component';

import { useEffect, useState } from 'react';

const Root = styled('header', {
  position: 'relative',
  minHeight: 'calc(100vh - var(--header-height))',

  /* hack: align the header content below the back button */
  paddingBlockStart: 'calc($7 + $5 * 2)',
  paddingInline: '$3',

  display: 'flex',
  justifyContent: 'center',
  alignItems: 'flex-start',
  flexDirection: 'column',

  color: 'var(--color-text-light)',

  backgroundRepeat: 'no-repeat',
  backgroundSize: 'cover',
  backgroundPosition: 'center',

  '@bp9': {
    backgroundSize: 'auto 100%',
    alignItems: 'center',
    textAlign: 'center',
    paddingBlockStart: '$7',
  },
});

export const IntroAnimationOverlay = styled('div', {
  position: 'absolute',
  insetBlockStart: 0,
  insetInlineStart: 0,
  width: '100%',
  height: '100%',
  backgroundColor: 'var(--color-background)',
  borderRadius: '$space$3',
  pointerEvents: 'none',
  transitionProperty: 'opacity',
  transitionDuration: '750ms',
  transitionTimingFunction: 'ease-out',
  variants: {
    hidden: {
      true: {
        opacity: 0,
      },
      false: {
        opacity: 1,
      },
    },
  },
});

export const StoryTheme = styled('p', {
  marginBlockEnd: '$3',
  fontSize: '$2',
  fontWeight: '$bold',
  textTransform: 'uppercase',
  letterSpacing: '0.1em',
  '[dir=rtl] &': {
    letterSpacing: 0,
  },
});

export const StoryTitle = styled('h1', {
  marginBlockEnd: '$6',
  '[lang=ta] &': {
    fontSize: '$4',
    lineHeight: '$3',
  },
});

export const StoryTagline = styled('p', {
  fontWeight: '$semibold',
  fontSize: '$4',
  maxWidth: '35ch',
  lineHeight: '$3',
  '[lang=ta] &': {
    fontSize: '$3',
  },
});

export const StoryHeader = (props: ComposableStyledComponentProps & { backgroundId: string }) => {
  const { backgroundId, children, css, ...rest } = props;

  const previousRoute = useStore((state) => state.previousRoute);
  const [backgroundUrls, setBackgroundUrls] = useState<[small?: string, regular?: string]>([]);
  const hasIntroAnimation = previousRoute === '/stories';
  const [isOverlayHidden, setOverlayHidden] = useState(hasIntroAnimation);

  useEffect(() => {
    if (hasIntroAnimation) {
      const id = setTimeout(() => {
        setOverlayHidden(true);
      }, 250);
      return () => {
        clearTimeout(id);
      };
    }
  }, [hasIntroAnimation]);

  useEffect(() => {
    let cancelled = false;

    // TODO: make these statically injected instead of dynamically
    const getBackgrounds = async () => {
      try {
        const small = await import(`@/assets/svg/story-background/${backgroundId}-small.svg`);
        const regular = await import(`@/assets/svg/story-background/${backgroundId}.svg`);

        if (!cancelled) {
          setBackgroundUrls([small.default, regular.default]);
        }
      } catch (error) {
        // ignore
      }
    };

    if (backgroundId) getBackgrounds();

    return () => {
      cancelled = true;
    };
  }, [backgroundId]);

  return (
    <Root
      css={{
        ...css,
        backgroundImage: `url(${backgroundUrls[0]})`,
        '@bp7': {
          backgroundImage: `url(${backgroundUrls[1]})`,
        },
      }}
      {...rest}
    >
      {children}
      {hasIntroAnimation && <IntroAnimationOverlay hidden={isOverlayHidden} />}
    </Root>
  );
};
